import { useRef } from 'react';
import { useAccountInfo } from '../api/queryHooks/account';
import { isStaff } from '../auth/roles';
import { useLogin } from './useLogin';

/**
 * The single client-side decision "this user must confirm their email before
 * going on" (docs/superpowers/specs/2026-11-03-verified-email-required-design.md).
 *
 * `pending` = the shop requires verified email and the signed-in person is an
 * unverified customer. Guests (governed by album_access only), staff and an
 * admin impersonating a user are never pending — the server agrees. Callers
 * decide *what* a pending user may still see; none re-derives pending itself.
 * `ready`: both answers (session, shop) are known, so `pending: false` is real.
 */
export const useVerificationWall = (): { pending: boolean; ready: boolean } => {
  const { userData, isPending: loginPending, isLoggedIn, isGuest, impersonation } = useLogin();
  const { data: accountInfo, isError: accountError } = useAccountInfo();

  // The account info is cached per UI language, so a language switch makes it
  // briefly undefined again. Keep the last known setting meanwhile: a guard that
  // flipped to "not ready" would unmount the page (and its state) on every switch.
  const lastKnown = useRef<boolean | undefined>(undefined);
  if (accountInfo) lastKnown.current = accountInfo.setting?.require_verified_email === true;
  const required = lastKnown.current === true;
  const settingKnown = lastKnown.current !== undefined || accountError;
  const pending =
    required &&
    isLoggedIn &&
    !isGuest &&
    userData?.email_verified === false &&
    !isStaff(userData?.roles) &&
    !impersonation;

  return { pending, ready: !loginPending && settingKnown };
};
